import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, X, ChevronDown, ChevronUp, Edit2, Check, Clock, AlertCircle, Calendar, Trash2 } from 'lucide-react';
import {
  getPlayers,
  getMedicalOptions, createMedicalOption, updateMedicalOption,
  getInjuries, createInjury, updateInjury, deleteInjury, updateInjuryStage, getInjuryHistory, addInjuryNote, getInjuryNotes, updateInjuryNote, deleteInjuryNote,
  getIllnesses, createIllness, updateIllness, addIllnessNote, getIllnessNotes, updateIllnessNote, deleteIllnessNote,
  getAppointments, createAppointment, updateAppointment,
  getMedicalLog, createMedicalLog, updateMedicalLog, deleteMedicalLog,
} from '../api';
import { useUrlParam } from '../hooks/useUrlParam';
import { STAGES, stageMeta, daysSince, fmtDate } from './medicalShared';

// ── Constants ─────────────────────────────────────────────────────────────────
// STAGES/stageMeta/daysSince/fmtDate moved to medicalShared.js (2026-09-10) so MedicalPanel.js
// (the Einstein compose panel) reuses the exact same stage metadata — same extraction pattern as
// gps/MatchChronic.js -> gps/matchChronicShared.js. Behavior here is unchanged.
// Local calendar date — toISOString() gives the UTC date (yesterday until 01:00/02:00 NL time).
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const MECHANISMS = ['Contact', 'Non-contact', 'Overuse', 'Other'];
const SIDES      = ['Left', 'Right', 'N/A'];
const CONTEXTS   = ['Training', 'Match', 'Other'];
const ILLNESS_STATUSES = ['reported', 'recovering', 'cleared'];
const APPT_STATUSES    = ['scheduled', 'completed', 'cancelled'];

function fmtDateTime(d) {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit' }) + ' ' +
    dt.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
}

// ── Shared UI ─────────────────────────────────────────────────────────────────
const card = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 16px', marginBottom: 10 };
const btn  = (active, color = '#3b82f6') => ({
  padding: '6px 12px', borderRadius: 8, border: `1px solid ${active ? color : 'var(--border)'}`,
  background: active ? color : 'transparent', color: active ? '#fff' : 'var(--text-secondary)',
  fontSize: 12, fontWeight: active ? 600 : 400, cursor: 'pointer', fontFamily: 'inherit',
});
const fieldStyle = { display: 'flex', flexDirection: 'column', gap: 4 };
const labelStyle = { fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' };
const inputStyle = {
  padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)',
  background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 13,
  fontFamily: 'inherit', width: '100%', boxSizing: 'border-box',
};
const selectStyle = { ...inputStyle };

function Field({ label, children }) {
  return <div style={fieldStyle}><label style={labelStyle}>{label}</label>{children}</div>;
}

function StageBadge({ stage }) {
  const m = stageMeta(stage);
  return (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 700, background: m.color + '22', color: m.color, border: `1px solid ${m.color}44` }}>
      {m.label}
    </span>
  );
}

function StageProgress({ current }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginTop: 6 }}>
      {STAGES.map((s, i) => {
        const active = s.id === current;
        const done   = STAGES.findIndex(x => x.id === current) > i;
        return (
          <React.Fragment key={s.id}>
            <div style={{ textAlign: 'center', flex: 1 }}>
              <div style={{
                width: 10, height: 10, borderRadius: '50%', margin: '0 auto 3px',
                background: active ? s.color : done ? '#334155' : 'var(--border)',
                border: active ? `2px solid ${s.color}` : 'none',
                boxShadow: active ? `0 0 0 3px ${s.color}33` : 'none',
              }} />
              <div style={{ fontSize: 8, color: active ? s.color : 'var(--text-dimmed)', fontWeight: active ? 700 : 400, whiteSpace: 'nowrap' }}>{s.label}</div>
            </div>
            {i < STAGES.length - 1 && (
              <div style={{ flex: 0.5, height: 2, background: done ? '#334155' : 'var(--border)', marginBottom: 12 }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ── Modal wrapper ─────────────────────────────────────────────────────────────
function Modal({ title, onClose, children, wide }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 200, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto' }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: 16, width: '100%', maxWidth: wide ? 740 : 520, boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 4 }}><X size={18} /></button>
        </div>
        <div style={{ padding: '20px' }}>{children}</div>
      </div>
    </div>
  );
}

// ── Log Injury modal ──────────────────────────────────────────────────────────
function InjuryModal({ players, options, user, onSave, onClose }) {
  const bodyParts   = options.filter(o => o.category === 'body_part');
  const injTypes    = options.filter(o => o.category === 'injury_type');

  const [form, setForm] = useState({
    player_id: '', date_injured: todayStr(),
    body_part: '', injury_type: '', mechanism: 'Non-contact', side: 'Left',
    context: 'Training', severity_grade: '', expected_return_date: '', diagnostic_notes: '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.player_id || !form.body_part || !form.injury_type) return;
    setSaving(true);
    try {
      await onSave({ ...form, severity_grade: form.severity_grade || null, created_by: user?.id });
      onClose();
    } finally { setSaving(false); }
  };

  return (
    <Modal title="Log new injury" onClose={onClose} wide>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Field label="Player">
          <select style={selectStyle} value={form.player_id} onChange={e => set('player_id', e.target.value)}>
            <option value="">— select —</option>
            {players.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
        <Field label="Date injured">
          <input style={inputStyle} type="date" value={form.date_injured} onChange={e => set('date_injured', e.target.value)} />
        </Field>
        <Field label="Body part">
          <select style={selectStyle} value={form.body_part} onChange={e => set('body_part', e.target.value)}>
            <option value="">— select —</option>
            {bodyParts.map(o => <option key={o.id}>{o.label}</option>)}
          </select>
        </Field>
        <Field label="Injury type">
          <select style={selectStyle} value={form.injury_type} onChange={e => set('injury_type', e.target.value)}>
            <option value="">— select —</option>
            {injTypes.map(o => <option key={o.id}>{o.label}</option>)}
          </select>
        </Field>
        <Field label="Mechanism">
          <select style={selectStyle} value={form.mechanism} onChange={e => set('mechanism', e.target.value)}>
            {MECHANISMS.map(m => <option key={m}>{m}</option>)}
          </select>
        </Field>
        <Field label="Side">
          <select style={selectStyle} value={form.side} onChange={e => set('side', e.target.value)}>
            {SIDES.map(s => <option key={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Context">
          <select style={selectStyle} value={form.context} onChange={e => set('context', e.target.value)}>
            {CONTEXTS.map(c => <option key={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Severity grade (1–3)">
          <select style={selectStyle} value={form.severity_grade} onChange={e => set('severity_grade', e.target.value)}>
            <option value="">— none —</option>
            <option value="1">1 — Mild</option>
            <option value="2">2 — Moderate</option>
            <option value="3">3 — Severe</option>
          </select>
        </Field>
        <Field label="Expected return date">
          <input style={inputStyle} type="date" value={form.expected_return_date} onChange={e => set('expected_return_date', e.target.value)} />
        </Field>
        <div />
        <div style={{ gridColumn: '1/-1' }}>
          <Field label="Diagnostic notes">
            <textarea style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }} value={form.diagnostic_notes} onChange={e => set('diagnostic_notes', e.target.value)} />
          </Field>
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
        <button style={btn(false)} onClick={onClose}>Cancel</button>
        <button style={btn(true)} onClick={handleSave} disabled={saving || !form.player_id || !form.body_part || !form.injury_type}>
          {saving ? 'Saving…' : 'Log injury'}
        </button>
      </div>
    </Modal>
  );
}

// ── Injury row (expandable) ───────────────────────────────────────────────────
function InjuryRow({ injury, user, options, onUpdate }) {
  const bodyParts = options.filter(o => o.category === 'body_part');
  const injTypes  = options.filter(o => o.category === 'injury_type');

  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [notes, setNotes]     = useState([]);
  const [noteText, setNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [editRD, setEditRD]   = useState('');
  const [editDN, setEditDN]   = useState('');
  const [editBodyPart, setEditBodyPart]           = useState('');
  const [editInjuryType, setEditInjuryType]       = useState('');
  const [editMechanism, setEditMechanism]         = useState('');
  const [editSide, setEditSide]                   = useState('');
  const [editContext, setEditContext]             = useState('');
  const [editSeverity, setEditSeverity]           = useState('');
  const [editingMeta, setEditingMeta] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editNoteText, setEditNoteText]   = useState('');
  const [deleting, setDeleting]           = useState(false);

  const days = daysSince(injury.updated_at || injury.created_at);

  const load = useCallback(async () => {
    const [h, n] = await Promise.all([getInjuryHistory(injury.id), getInjuryNotes(injury.id)]);
    setHistory(h);
    setNotes(n);
  }, [injury.id]);

  const toggle = () => {
    if (!open) load();
    setOpen(o => !o);
  };

  const changeStage = async (stage) => {
    await updateInjuryStage(injury.id, { stage, changed_by: user?.id });
    onUpdate();
    load();
  };

  const saveNote = async () => {
    if (!noteText.trim()) return;
    setAddingNote(true);
    await addInjuryNote(injury.id, { note: noteText.trim(), created_by: user?.id });
    setNoteText('');
    setAddingNote(false);
    load();
  };

  const openEditNote = (n) => {
    setEditingNoteId(n.id);
    setEditNoteText(n.note);
  };

  const saveEditNote = async () => {
    if (!editNoteText.trim()) return;
    await updateInjuryNote(editingNoteId, { note: editNoteText.trim() });
    setEditingNoteId(null);
    load();
  };

  const removeNote = async (id) => {
    await deleteInjuryNote(id);
    load();
  };

  // For a record logged in error. Closing an injury still counts its whole span as a real
  // absence, so there was previously no way to undo one that never happened. The confirm spells
  // out the availability consequence because the effect is retroactive and not otherwise visible
  // from this page.
  const removeInjury = async () => {
    const label = `${injury.body_part} - ${injury.injury_type} (${fmtDate(injury.date_injured)})`;
    if (!window.confirm(
      `Delete this injury?\n\n${injury.player_name ? injury.player_name + '\n' : ''}${label}\n\n` +
      `Its stage history, notes and linked appointments are deleted too, and the player's ` +
      `availability for these dates is recalculated as if the injury never happened.\n\n` +
      `This cannot be undone.`
    )) return;
    setDeleting(true);
    try {
      await deleteInjury(injury.id);
      onUpdate();
    } catch (err) {
      alert(`Could not delete this injury: ${err.message}`);
      setDeleting(false);
    }
  };

  const saveMeta = async () => {
    await updateInjury(injury.id, {
      body_part: editBodyPart,
      injury_type: editInjuryType,
      mechanism: editMechanism,
      side: editSide,
      context: editContext,
      severity_grade: editSeverity || null,
      expected_return_date: editRD || null,
      diagnostic_notes: editDN || null,
    });
    setEditingMeta(false);
    onUpdate();
  };

  const openEdit = () => {
    setEditRD(injury.expected_return_date ? injury.expected_return_date.slice(0, 10) : '');
    setEditDN(injury.diagnostic_notes || '');
    setEditBodyPart(injury.body_part || '');
    setEditInjuryType(injury.injury_type || '');
    setEditMechanism(injury.mechanism || '');
    setEditSide(injury.side || '');
    setEditContext(injury.context || '');
    setEditSeverity(injury.severity_grade || '');
    setEditingMeta(true);
  };

  const stage = stageMeta(injury.current_stage);
  const stageIdx = STAGES.findIndex(s => s.id === injury.current_stage);
  const canAdvance = stageIdx < STAGES.length - 1 && !injury.is_closed;
  const canRevert  = stageIdx > 0 && !injury.is_closed;

  return (
    <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
      <div onClick={toggle} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', cursor: 'pointer' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: 14 }}>{injury.player_name}</span>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{injury.body_part} · {injury.injury_type}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
            <StageBadge stage={injury.current_stage} />
            <span style={{ fontSize: 11, color: 'var(--text-dimmed)' }}>{fmtDate(injury.date_injured)}</span>
            {injury.expected_return_date && (
              <span style={{ fontSize: 11, color: '#f59e0b' }}>ERD {fmtDate(injury.expected_return_date)}</span>
            )}
            {days != null && <span style={{ fontSize: 11, color: 'var(--text-dimmed)' }}>{days}d in stage</span>}
            {injury.is_closed && <span style={{ fontSize: 11, color: '#10b981', fontWeight: 700 }}>Closed</span>}
          </div>
        </div>
        <span style={{ color: 'var(--text-muted)', flexShrink: 0 }}>
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </span>
      </div>

      {open && (
        <div style={{ padding: '0 16px 16px', borderTop: '1px solid var(--border)' }}>
          {/* Meta row */}
          {!editingMeta && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 14, marginBottom: 14 }}>
              <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 8 }}>
                {[
                  ['Body part',  injury.body_part],
                  ['Injury type', injury.injury_type],
                  ['Mechanism', injury.mechanism],
                  ['Side',      injury.side],
                  ['Context',   injury.context],
                  ['Severity',  injury.severity_grade ? `Grade ${injury.severity_grade}` : '—'],
                  ['Injured',   fmtDate(injury.date_injured)],
                  ['Returned',  injury.actual_return_date ? fmtDate(injury.actual_return_date) : '—'],
                ].map(([k, v]) => (
                  <div key={k} style={{ background: 'var(--bg-app)', borderRadius: 8, padding: '8px 10px' }}>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 2 }}>{k}</div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{v || '—'}</div>
                  </div>
                ))}
              </div>
              <button style={btn(false)} onClick={openEdit} title="Edit injury details"><Edit2 size={12} /></button>
              <button style={btn(false, '#ef4444')} onClick={removeInjury} disabled={deleting}
                title="Delete this injury — for a record logged in error">
                <Trash2 size={12} />
              </button>
            </div>
          )}

          {/* Stage progress */}
          <StageProgress current={injury.current_stage} />

          {/* Stage controls */}
          {!injury.is_closed && (
            <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
              {canRevert && (
                <button style={btn(false, '#f59e0b')} onClick={() => changeStage(STAGES[stageIdx - 1].id)}>
                  ← {STAGES[stageIdx - 1].label}
                </button>
              )}
              {canAdvance && (
                <button style={btn(true, stage.color)} onClick={() => changeStage(STAGES[stageIdx + 1].id)}>
                  → {STAGES[stageIdx + 1].label}
                </button>
              )}
              {injury.current_stage !== 'rtc' && (
                <button style={{ ...btn(false, '#ef4444'), marginLeft: 'auto' }} onClick={() => changeStage('rtc')}>
                  Close (RTC)
                </button>
              )}
            </div>
          )}

          {/* Details + notes edit */}
          {!editingMeta ? (
            <div style={{ marginTop: 0, fontSize: 13, color: 'var(--text-secondary)', fontStyle: injury.diagnostic_notes ? 'normal' : 'italic' }}>
              {injury.diagnostic_notes || 'No diagnostic notes.'}
            </div>
          ) : (
            <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <Field label="Body part">
                  <select style={selectStyle} value={editBodyPart} onChange={e => setEditBodyPart(e.target.value)}>
                    <option value="">— select —</option>
                    {bodyParts.map(o => <option key={o.id}>{o.label}</option>)}
                  </select>
                </Field>
                <Field label="Injury type">
                  <select style={selectStyle} value={editInjuryType} onChange={e => setEditInjuryType(e.target.value)}>
                    <option value="">— select —</option>
                    {injTypes.map(o => <option key={o.id}>{o.label}</option>)}
                  </select>
                </Field>
                <Field label="Mechanism">
                  <select style={selectStyle} value={editMechanism} onChange={e => setEditMechanism(e.target.value)}>
                    {MECHANISMS.map(m => <option key={m}>{m}</option>)}
                  </select>
                </Field>
                <Field label="Side">
                  <select style={selectStyle} value={editSide} onChange={e => setEditSide(e.target.value)}>
                    {SIDES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </Field>
                <Field label="Context">
                  <select style={selectStyle} value={editContext} onChange={e => setEditContext(e.target.value)}>
                    {CONTEXTS.map(c => <option key={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Severity grade (1–3)">
                  <select style={selectStyle} value={editSeverity} onChange={e => setEditSeverity(e.target.value)}>
                    <option value="">— none —</option>
                    <option value="1">1 — Mild</option>
                    <option value="2">2 — Moderate</option>
                    <option value="3">3 — Severe</option>
                  </select>
                </Field>
              </div>
              <Field label="Expected return date">
                <input style={inputStyle} type="date" value={editRD} onChange={e => setEditRD(e.target.value)} />
              </Field>
              <Field label="Diagnostic notes">
                <textarea style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }} value={editDN} onChange={e => setEditDN(e.target.value)} />
              </Field>
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={btn(false)} onClick={() => setEditingMeta(false)}>Cancel</button>
                <button style={btn(true)} onClick={saveMeta} disabled={!editBodyPart || !editInjuryType}>Save</button>
              </div>
            </div>
          )}

          {/* Stage history */}
          {history.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Stage history</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {history.map(h => (
                  <div key={h.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                    <Clock size={11} style={{ color: 'var(--text-dimmed)', flexShrink: 0 }} />
                    <StageBadge stage={h.stage} />
                    <span style={{ color: 'var(--text-muted)' }}>{fmtDate(h.changed_at)}</span>
                    {h.changed_by_name && <span style={{ color: 'var(--text-dimmed)' }}>by {h.changed_by_name}</span>}
                    {h.notes && <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>{h.notes}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes log */}
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Physio notes</div>
            {notes.map(n => (
              <div key={n.id} style={{ padding: '8px 10px', background: 'var(--bg-app)', borderRadius: 8, marginBottom: 6 }}>
                {editingNoteId === n.id ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <textarea
                      style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }}
                      value={editNoteText}
                      onChange={e => setEditNoteText(e.target.value)}
                      autoFocus
                    />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button style={btn(false)} onClick={() => setEditingNoteId(null)}>Cancel</button>
                      <button style={btn(true)} onClick={saveEditNote} disabled={!editNoteText.trim()}>Save</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{n.note}</div>
                      <div style={{ fontSize: 10, color: 'var(--text-dimmed)', marginTop: 3 }}>
                        {fmtDate(n.created_at)}{n.author ? ` · ${n.author}` : ''}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                      <button style={{ ...btn(false), padding: 6 }} onClick={() => openEditNote(n)} title="Edit note"><Edit2 size={11} /></button>
                      <button style={{ ...btn(false, '#ef4444'), padding: 6 }} onClick={() => removeNote(n.id)} title="Delete note"><Trash2 size={11} /></button>
                    </div>
                  </div>
                )}
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <input
                style={{ ...inputStyle, flex: 1 }}
                placeholder="Add note…"
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && saveNote()}
              />
              <button style={btn(true)} onClick={saveNote} disabled={addingNote || !noteText.trim()}>
                {addingNote ? '…' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Injuries tab ──────────────────────────────────────────────────────────────
function InjuriesTab({ user, players, options, injuries, onRefresh }) {
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter]       = useUrlParam('status', 'active'); // all / active / closed
  const [search, setSearch]       = useUrlParam('search', '');

  const filtered = injuries
    .filter(i => filter === 'all' ? true : filter === 'active' ? !i.is_closed : i.is_closed)
    .filter(i => !search || i.player_name?.toLowerCase().includes(search.toLowerCase()));

  const handleCreate = async (data) => {
    await createInjury(data);
    onRefresh();
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <button style={btn(true)} onClick={() => setShowModal(true)}><Plus size={14} style={{ marginRight: 4 }} />Log new injury</button>
        <div style={{ display: 'flex', gap: 4 }}>
          {['active', 'all', 'closed'].map(f => (
            <button key={f} style={btn(filter === f)} onClick={() => setFilter(f)}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
        <input
          style={{ ...inputStyle, width: 200 }}
          placeholder="Search player…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {filtered.length === 0
        ? <div style={{ textAlign: 'center', color: 'var(--text-dimmed)', padding: 40 }}>No injuries found.</div>
        : filtered.map(i => <InjuryRow key={i.id} injury={i} user={user} options={options} onUpdate={onRefresh} />)
      }

      {showModal && (
        <InjuryModal players={players} options={options} user={user} onSave={handleCreate} onClose={() => setShowModal(false)} />
      )}
    </div>
  );
}

// ── Illness modal ─────────────────────────────────────────────────────────────
function IllnessModal({ players, options, user, onSave, onClose }) {
  const illnessTypes = options.filter(o => o.category === 'illness_type');
  const [form, setForm] = useState({
    player_id: '', date_reported: todayStr(),
    illness_type: '', contagious: false, expected_return_date: '', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.player_id || !form.illness_type) return;
    setSaving(true);
    try {
      await onSave({ ...form, created_by: user?.id });
      onClose();
    } finally { setSaving(false); }
  };

  return (
    <Modal title="Report illness" onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Field label="Player">
          <select style={selectStyle} value={form.player_id} onChange={e => set('player_id', e.target.value)}>
            <option value="">— select —</option>
            {players.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
        <Field label="Date reported">
          <input style={inputStyle} type="date" value={form.date_reported} onChange={e => set('date_reported', e.target.value)} />
        </Field>
        <Field label="Illness type">
          <select style={selectStyle} value={form.illness_type} onChange={e => set('illness_type', e.target.value)}>
            <option value="">— select —</option>
            {illnessTypes.map(o => <option key={o.id}>{o.label}</option>)}
          </select>
        </Field>
        <Field label="Contagious?">
          <div style={{ display: 'flex', gap: 8 }}>
            {[['Yes', true], ['No', false]].map(([l, v]) => (
              <button key={l} type="button" style={btn(form.contagious === v, v ? '#ef4444' : '#10b981')} onClick={() => set('contagious', v)}>{l}</button>
            ))}
          </div>
        </Field>
        <Field label="Expected return date">
          <input style={inputStyle} type="date" value={form.expected_return_date} onChange={e => set('expected_return_date', e.target.value)} />
        </Field>
        <Field label="Notes">
          <textarea style={{ ...inputStyle, minHeight: 70, resize: 'vertical' }} value={form.notes} onChange={e => set('notes', e.target.value)} />
        </Field>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
        <button style={btn(false)} onClick={onClose}>Cancel</button>
        <button style={btn(true)} onClick={handleSave} disabled={saving || !form.player_id || !form.illness_type}>
          {saving ? 'Saving…' : 'Report'}
        </button>
      </div>
    </Modal>
  );
}

// ── Illness row ───────────────────────────────────────────────────────────────
function IllnessRow({ illness, user, onUpdate }) {
  const [open, setOpen] = useState(false);
  const [editStatus, setEditStatus] = useState(illness.status);
  const [editERD, setEditERD] = useState(illness.expected_return_date ? illness.expected_return_date.slice(0, 10) : '');
  const [editNotes, setEditNotes] = useState(illness.notes || '');
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editNoteText, setEditNoteText] = useState('');

  const statusColor = { reported: '#ef4444', recovering: '#f59e0b', cleared: '#10b981' };

  const loadNotes = useCallback(async () => {
    setNotes(await getIllnessNotes(illness.id));
  }, [illness.id]);

  const toggle = () => {
    if (!open) loadNotes();
    setOpen(o => !o);
  };

  const handleSave = async () => {
    setSaving(true);
    await updateIllness(illness.id, { status: editStatus, expected_return_date: editERD || null, notes: editNotes || null });
    setSaving(false);
    onUpdate();
  };

  const saveNote = async () => {
    if (!noteText.trim()) return;
    setAddingNote(true);
    await addIllnessNote(illness.id, { note: noteText.trim(), created_by: user?.id });
    setNoteText('');
    setAddingNote(false);
    loadNotes();
  };

  const openEditNote = (n) => {
    setEditingNoteId(n.id);
    setEditNoteText(n.note);
  };

  const saveEditNote = async () => {
    if (!editNoteText.trim()) return;
    await updateIllnessNote(editingNoteId, { note: editNoteText.trim() });
    setEditingNoteId(null);
    loadNotes();
  };

  const removeNote = async (id) => {
    await deleteIllnessNote(id);
    loadNotes();
  };

  return (
    <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
      <div onClick={toggle} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', cursor: 'pointer' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: 14 }}>{illness.player_name}</span>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{illness.illness_type}</span>
            {illness.contagious && <span style={{ fontSize: 11, color: '#ef4444', fontWeight: 700 }}>⚠ Contagious</span>}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: statusColor[illness.status] || 'var(--text-muted)' }}>
              {illness.status}
            </span>
            <span style={{ fontSize: 11, color: 'var(--text-dimmed)' }}>{fmtDate(illness.date_reported)}</span>
            {illness.expected_return_date && <span style={{ fontSize: 11, color: '#f59e0b' }}>ERD {fmtDate(illness.expected_return_date)}</span>}
          </div>
        </div>
        {open ? <ChevronUp size={16} style={{ color: 'var(--text-muted)' }} /> : <ChevronDown size={16} style={{ color: 'var(--text-muted)' }} />}
      </div>
      {open && (
        <div style={{ padding: '0 16px 16px', borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}>
          <Field label="Status">
            <div style={{ display: 'flex', gap: 6 }}>
              {ILLNESS_STATUSES.map(s => (
                <button key={s} style={btn(editStatus === s, statusColor[s])} onClick={() => setEditStatus(s)}>{s}</button>
              ))}
            </div>
          </Field>
          <Field label="Expected return date">
            <input style={inputStyle} type="date" value={editERD} onChange={e => setEditERD(e.target.value)} />
          </Field>
          <Field label="Notes">
            <textarea style={{ ...inputStyle, minHeight: 70, resize: 'vertical' }} value={editNotes} onChange={e => setEditNotes(e.target.value)} />
          </Field>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button style={btn(true)} onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          </div>

          {/* Notes log */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Physio notes</div>
            {notes.map(n => (
              <div key={n.id} style={{ padding: '8px 10px', background: 'var(--bg-app)', borderRadius: 8, marginBottom: 6 }}>
                {editingNoteId === n.id ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <textarea
                      style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }}
                      value={editNoteText}
                      onChange={e => setEditNoteText(e.target.value)}
                      autoFocus
                    />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button style={btn(false)} onClick={() => setEditingNoteId(null)}>Cancel</button>
                      <button style={btn(true)} onClick={saveEditNote} disabled={!editNoteText.trim()}>Save</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{n.note}</div>
                      <div style={{ fontSize: 10, color: 'var(--text-dimmed)', marginTop: 3 }}>
                        {fmtDate(n.created_at)}{n.author ? ` · ${n.author}` : ''}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                      <button style={{ ...btn(false), padding: 6 }} onClick={() => openEditNote(n)} title="Edit note"><Edit2 size={11} /></button>
                      <button style={{ ...btn(false, '#ef4444'), padding: 6 }} onClick={() => removeNote(n.id)} title="Delete note"><Trash2 size={11} /></button>
                    </div>
                  </div>
                )}
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <input
                style={{ ...inputStyle, flex: 1 }}
                placeholder="Add note…"
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && saveNote()}
              />
              <button style={btn(true)} onClick={saveNote} disabled={addingNote || !noteText.trim()}>
                {addingNote ? '…' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Illness tab ───────────────────────────────────────────────────────────────
function IllnessTab({ user, players, options, illnesses, onRefresh }) {
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter]       = useUrlParam('status', 'active');

  const filtered = illnesses.filter(i =>
    filter === 'all' ? true : filter === 'active' ? i.status !== 'cleared' : i.status === 'cleared'
  );

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <button style={btn(true)} onClick={() => setShowModal(true)}><Plus size={14} style={{ marginRight: 4 }} />Report illness</button>
        <div style={{ display: 'flex', gap: 4 }}>
          {['active', 'all', 'cleared'].map(f => (
            <button key={f} style={btn(filter === f)} onClick={() => setFilter(f)}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>
      {filtered.length === 0
        ? <div style={{ textAlign: 'center', color: 'var(--text-dimmed)', padding: 40 }}>No illnesses found.</div>
        : filtered.map(i => <IllnessRow key={i.id} illness={i} user={user} onUpdate={onRefresh} />)
      }
      {showModal && (
        <IllnessModal players={players} options={options} user={user} onSave={data => { createIllness(data).then(onRefresh); }} onClose={() => setShowModal(false)} />
      )}
    </div>
  );
}

// ── Appointment modal ─────────────────────────────────────────────────────────
function AppointmentModal({ players, options, injuries, illnesses, user, onSave, onClose }) {
  const apptTypes = options.filter(o => o.category === 'appointment_type');
  const [form, setForm] = useState({
    player_id: '', appointment_type: '', appointment_date: '', location: '',
    notes: '', injury_id: '', illness_id: '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const playerInjuries  = injuries.filter(i => String(i.player_id) === String(form.player_id) && !i.is_closed);
  const playerIllnesses = illnesses.filter(i => String(i.player_id) === String(form.player_id) && i.status !== 'cleared');

  const handleSave = async () => {
    if (!form.player_id || !form.appointment_type || !form.appointment_date) return;
    setSaving(true);
    try {
      // datetime-local's value has no offset; new Date() reads it as browser-local time, so
      // toISOString() sends the correct UTC instant instead of a naive string the UTC server misreads.
      await onSave({ ...form, appointment_date: new Date(form.appointment_date).toISOString(), created_by: user?.id });
      onClose();
    } finally { setSaving(false); }
  };

  return (
    <Modal title="Schedule appointment" onClose={onClose} wide>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Field label="Player">
          <select style={selectStyle} value={form.player_id} onChange={e => set('player_id', e.target.value)}>
            <option value="">— select —</option>
            {players.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
        <Field label="Appointment type">
          <select style={selectStyle} value={form.appointment_type} onChange={e => set('appointment_type', e.target.value)}>
            <option value="">— select —</option>
            {apptTypes.map(o => <option key={o.id}>{o.label}</option>)}
          </select>
        </Field>
        <Field label="Date & time">
          <input style={inputStyle} type="datetime-local" value={form.appointment_date} onChange={e => set('appointment_date', e.target.value)} />
        </Field>
        <Field label="Location">
          <input style={inputStyle} type="text" placeholder="e.g. Medisch Spectrum Twente" value={form.location} onChange={e => set('location', e.target.value)} />
        </Field>
        {playerInjuries.length > 0 && (
          <Field label="Link to injury (optional)">
            <select style={selectStyle} value={form.injury_id} onChange={e => set('injury_id', e.target.value)}>
              <option value="">— none —</option>
              {playerInjuries.map(i => <option key={i.id} value={i.id}>{i.body_part} — {i.injury_type}</option>)}
            </select>
          </Field>
        )}
        {playerIllnesses.length > 0 && (
          <Field label="Link to illness (optional)">
            <select style={selectStyle} value={form.illness_id} onChange={e => set('illness_id', e.target.value)}>
              <option value="">— none —</option>
              {playerIllnesses.map(i => <option key={i.id} value={i.id}>{i.illness_type}</option>)}
            </select>
          </Field>
        )}
        <div style={{ gridColumn: '1/-1' }}>
          <Field label="Notes">
            <textarea style={{ ...inputStyle, minHeight: 70, resize: 'vertical' }} value={form.notes} onChange={e => set('notes', e.target.value)} />
          </Field>
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
        <button style={btn(false)} onClick={onClose}>Cancel</button>
        <button style={btn(true)} onClick={handleSave} disabled={saving || !form.player_id || !form.appointment_type || !form.appointment_date}>
          {saving ? 'Saving…' : 'Schedule'}
        </button>
      </div>
    </Modal>
  );
}

// ── Appointments tab ──────────────────────────────────────────────────────────
function AppointmentsTab({ user, players, options, appointments, injuries, illnesses, onRefresh }) {
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter]       = useUrlParam('status', 'upcoming');
  const now = new Date();

  const sorted = [...appointments].sort((a, b) => new Date(a.appointment_date) - new Date(b.appointment_date));
  const filtered = sorted.filter(a => {
    const d = new Date(a.appointment_date);
    if (filter === 'upcoming') return d >= now && a.status === 'scheduled';
    if (filter === 'past')     return d < now || a.status !== 'scheduled';
    return true;
  });

  const statusColor = { scheduled: '#3b82f6', completed: '#10b981', cancelled: '#64748b' };

  const changeStatus = async (id, status) => {
    await updateAppointment(id, { status });
    onRefresh();
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <button style={btn(true)} onClick={() => setShowModal(true)}><Plus size={14} style={{ marginRight: 4 }} />Schedule appointment</button>
        <div style={{ display: 'flex', gap: 4 }}>
          {['upcoming', 'all', 'past'].map(f => (
            <button key={f} style={btn(filter === f)} onClick={() => setFilter(f)}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0
        ? <div style={{ textAlign: 'center', color: 'var(--text-dimmed)', padding: 40 }}>No appointments found.</div>
        : filtered.map(a => (
          <div key={a.id} style={card}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <Calendar size={18} style={{ color: '#3b82f6', flexShrink: 0, marginTop: 1 }} />
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 700, fontSize: 14 }}>{a.player_name}</span>
                  <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{a.appointment_type}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: statusColor[a.status] }}>{a.status}</span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                  {fmtDateTime(a.appointment_date)}{a.location ? ` · ${a.location}` : ''}
                </div>
                {a.notes && <div style={{ fontSize: 12, color: 'var(--text-dimmed)', marginTop: 4, fontStyle: 'italic' }}>{a.notes}</div>}
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                {a.status === 'scheduled' && (
                  <>
                    <button style={btn(false, '#10b981')} onClick={() => changeStatus(a.id, 'completed')}><Check size={12} /></button>
                    <button style={btn(false, '#64748b')} onClick={() => changeStatus(a.id, 'cancelled')}><X size={12} /></button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))
      }

      {showModal && (
        <AppointmentModal
          players={players} options={options} injuries={injuries} illnesses={illnesses}
          user={user} onSave={data => { createAppointment(data).then(onRefresh); }} onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}

// ── History stats ─────────────────────────────────────────────────────────────
function computeInjuryStats(injuries) {
  const total = injuries.length;

  const groupBy = (key) => {
    const map = {};
    injuries.forEach(i => {
      const k = i[key] || 'Unknown';
      map[k] = (map[k] || 0) + 1;
    });
    return Object.entries(map)
      .map(([label, count]) => ({ label, count, pct: total ? Math.round((count / total) * 100) : 0 }))
      .sort((a, b) => b.count - a.count);
  };

  const closedWithReturn = injuries.filter(i => i.is_closed && i.actual_return_date);
  const avgDaysOut = closedWithReturn.length
    ? Math.round(
        closedWithReturn.reduce((sum, i) =>
          sum + Math.max(0, Math.round((new Date(i.actual_return_date) - new Date(i.date_injured)) / 86400000)), 0
        ) / closedWithReturn.length
      )
    : null;

  const playerCounts = {};
  injuries.forEach(i => { playerCounts[i.player_id] = (playerCounts[i.player_id] || 0) + 1; });
  const recurrentCount = Object.values(playerCounts).filter(c => c > 1).length;

  return {
    total,
    activeCount: injuries.filter(i => !i.is_closed).length,
    closedCount: injuries.filter(i => i.is_closed).length,
    avgDaysOut,
    recurrentCount,
    byBodyPart:   groupBy('body_part'),
    byInjuryType: groupBy('injury_type'),
    byMechanism:  groupBy('mechanism'),
    byContext:    groupBy('context'),
  };
}

function StatCard({ label, value, sub, color }) {
  return (
    <div style={{ background: 'var(--bg-app)', borderRadius: 10, padding: '12px 14px' }}>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: color || 'var(--text-primary)' }}>{value ?? '—'}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function BreakdownList({ title, items, color = '#3b82f6' }) {
  return (
    <div style={card}>
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>{title}</div>
      {items.length === 0
        ? <div style={{ fontSize: 12, color: 'var(--text-dimmed)' }}>No data yet.</div>
        : items.map(it => (
          <div key={it.label} style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
              <span style={{ color: 'var(--text-secondary)' }}>{it.label}</span>
              <span style={{ color: 'var(--text-muted)' }}>{it.count} · {it.pct}%</span>
            </div>
            <div style={{ height: 6, background: 'var(--border)', borderRadius: 99, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${it.pct}%`, background: color, borderRadius: 99 }} />
            </div>
          </div>
        ))
      }
    </div>
  );
}

function HistoryRow({ injury }) {
  const [open, setOpen] = useState(false);
  const days = injury.actual_return_date
    ? Math.max(0, Math.round((new Date(injury.actual_return_date) - new Date(injury.date_injured)) / 86400000))
    : null;

  return (
    <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', cursor: 'pointer' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: 14 }}>{injury.player_name}</span>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{injury.body_part} · {injury.injury_type}</span>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
            <StageBadge stage={injury.current_stage} />
            <span style={{ fontSize: 11, color: 'var(--text-dimmed)' }}>
              {fmtDate(injury.date_injured)} → {injury.actual_return_date ? fmtDate(injury.actual_return_date) : (injury.is_closed ? '—' : 'ongoing')}
            </span>
            {days != null && <span style={{ fontSize: 11, color: 'var(--text-dimmed)' }}>{days}d out</span>}
            <span style={{ fontSize: 11, color: 'var(--text-dimmed)' }}>{injury.mechanism}{injury.context ? ` · ${injury.context}` : ''}</span>
          </div>
        </div>
        {open ? <ChevronUp size={16} style={{ color: 'var(--text-muted)' }} /> : <ChevronDown size={16} style={{ color: 'var(--text-muted)' }} />}
      </div>
      {open && (
        <div style={{ padding: '0 16px 14px', borderTop: '1px solid var(--border)', marginTop: 4 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 8, marginTop: 10, marginBottom: 10 }}>
            {[
              ['Side', injury.side],
              ['Severity', injury.severity_grade ? `Grade ${injury.severity_grade}` : '—'],
              ['Expected return', injury.expected_return_date ? fmtDate(injury.expected_return_date) : '—'],
            ].map(([k, v]) => (
              <div key={k} style={{ background: 'var(--bg-app)', borderRadius: 8, padding: '6px 10px' }}>
                <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{k}</div>
                <div style={{ fontSize: 12, fontWeight: 600 }}>{v}</div>
              </div>
            ))}
          </div>
          {injury.diagnostic_notes && (
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontStyle: 'italic' }}>{injury.diagnostic_notes}</div>
          )}
        </div>
      )}
    </div>
  );
}

function computeIllnessStats(illnesses) {
  const total = illnesses.length;

  const groupBy = (key) => {
    const map = {};
    illnesses.forEach(i => {
      const k = i[key] || 'Unknown';
      map[k] = (map[k] || 0) + 1;
    });
    return Object.entries(map)
      .map(([label, count]) => ({ label, count, pct: total ? Math.round((count / total) * 100) : 0 }))
      .sort((a, b) => b.count - a.count);
  };

  const contagiousCount = illnesses.filter(i => i.contagious).length;

  return {
    total,
    activeCount: illnesses.filter(i => i.status !== 'cleared').length,
    clearedCount: illnesses.filter(i => i.status === 'cleared').length,
    contagiousCount,
    contagiousPct: total ? Math.round((contagiousCount / total) * 100) : 0,
    byType: groupBy('illness_type'),
  };
}

function IllnessHistoryRow({ illness }) {
  const [open, setOpen] = useState(false);
  const statusColor = { reported: '#ef4444', recovering: '#f59e0b', cleared: '#10b981' };

  return (
    <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', cursor: 'pointer' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: 14 }}>{illness.player_name}</span>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{illness.illness_type}</span>
            {illness.contagious && <span style={{ fontSize: 11, color: '#ef4444', fontWeight: 700 }}>⚠ Contagious</span>}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: statusColor[illness.status] || 'var(--text-muted)' }}>{illness.status}</span>
            <span style={{ fontSize: 11, color: 'var(--text-dimmed)' }}>
              {fmtDate(illness.date_reported)} → {illness.actual_return_date ? fmtDate(illness.actual_return_date) : (illness.status === 'cleared' ? '—' : 'ongoing')}
            </span>
          </div>
        </div>
        {open ? <ChevronUp size={16} style={{ color: 'var(--text-muted)' }} /> : <ChevronDown size={16} style={{ color: 'var(--text-muted)' }} />}
      </div>
      {open && illness.notes && (
        <div style={{ padding: '0 16px 14px', borderTop: '1px solid var(--border)', marginTop: 4 }}>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontStyle: 'italic', marginTop: 10 }}>{illness.notes}</div>
        </div>
      )}
    </div>
  );
}

// ── History tab ───────────────────────────────────────────────────────────────
function HistoryTab({ players, injuries, illnesses }) {
  const [selectedInjuryPlayer, setSelectedInjuryPlayer] = useUrlParam('injury_player', '');
  const [selectedIllnessPlayer, setSelectedIllnessPlayer] = useUrlParam('illness_player', '');

  const injStats = useMemo(() => computeInjuryStats(injuries), [injuries]);
  const illStats = useMemo(() => computeIllnessStats(illnesses), [illnesses]);

  const playersWithInjuries = useMemo(() => {
    const ids = new Set(injuries.map(i => String(i.player_id)));
    return players.filter(p => ids.has(String(p.id)));
  }, [players, injuries]);

  const playersWithIllnesses = useMemo(() => {
    const ids = new Set(illnesses.map(i => String(i.player_id)));
    return players.filter(p => ids.has(String(p.id)));
  }, [players, illnesses]);

  const filteredInjuries = useMemo(() => {
    const list = selectedInjuryPlayer ? injuries.filter(i => String(i.player_id) === selectedInjuryPlayer) : injuries;
    return [...list].sort((a, b) => new Date(b.date_injured) - new Date(a.date_injured));
  }, [injuries, selectedInjuryPlayer]);

  const filteredIllnesses = useMemo(() => {
    const list = selectedIllnessPlayer ? illnesses.filter(i => String(i.player_id) === selectedIllnessPlayer) : illnesses;
    return [...list].sort((a, b) => new Date(b.date_reported) - new Date(a.date_reported));
  }, [illnesses, selectedIllnessPlayer]);

  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Injuries</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 16 }}>
        <StatCard label="Total injuries" value={injStats.total} />
        <StatCard label="Active" value={injStats.activeCount} color="#ef4444" />
        <StatCard label="Closed (RTC)" value={injStats.closedCount} color="#10b981" />
        <StatCard label="Avg days out" value={injStats.avgDaysOut != null ? injStats.avgDaysOut : '—'} sub={injStats.avgDaysOut != null ? 'per closed injury' : null} />
        <StatCard label="Recurrent players" value={injStats.recurrentCount} sub="more than 1 injury" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14, marginBottom: 20 }}>
        <BreakdownList title="By body part"   items={injStats.byBodyPart}   color="#ef4444" />
        <BreakdownList title="By injury type" items={injStats.byInjuryType} color="#8b5cf6" />
        <BreakdownList title="By mechanism"   items={injStats.byMechanism}  color="#f59e0b" />
        <BreakdownList title="By context"     items={injStats.byContext}    color="#14b8a6" />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 700 }}>Log</span>
        <select style={{ ...selectStyle, width: 240 }} value={selectedInjuryPlayer} onChange={e => setSelectedInjuryPlayer(e.target.value)}>
          <option value="">All players ({injuries.length})</option>
          {playersWithInjuries.map(p => {
            const c = injuries.filter(i => String(i.player_id) === String(p.id)).length;
            return <option key={p.id} value={p.id}>{p.name} ({c})</option>;
          })}
        </select>
      </div>

      {filteredInjuries.length === 0
        ? <div style={{ textAlign: 'center', color: 'var(--text-dimmed)', padding: 40 }}>No injury history found.</div>
        : filteredInjuries.map(i => <HistoryRow key={i.id} injury={i} />)
      }

      <div style={{ height: 1, background: 'var(--border)', margin: '28px 0' }} />

      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Illness</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 16 }}>
        <StatCard label="Total illnesses" value={illStats.total} />
        <StatCard label="Active" value={illStats.activeCount} color="#f59e0b" />
        <StatCard label="Cleared" value={illStats.clearedCount} color="#10b981" />
        <StatCard label="Contagious" value={illStats.contagiousCount} sub={illStats.total ? `${illStats.contagiousPct}% of illnesses` : null} color="#ef4444" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14, marginBottom: 20 }}>
        <BreakdownList title="By illness type" items={illStats.byType} color="#f59e0b" />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 700 }}>Log</span>
        <select style={{ ...selectStyle, width: 240 }} value={selectedIllnessPlayer} onChange={e => setSelectedIllnessPlayer(e.target.value)}>
          <option value="">All players ({illnesses.length})</option>
          {playersWithIllnesses.map(p => {
            const c = illnesses.filter(i => String(i.player_id) === String(p.id)).length;
            return <option key={p.id} value={p.id}>{p.name} ({c})</option>;
          })}
        </select>
      </div>

      {filteredIllnesses.length === 0
        ? <div style={{ textAlign: 'center', color: 'var(--text-dimmed)', padding: 40 }}>No illness history found.</div>
        : filteredIllnesses.map(i => <IllnessHistoryRow key={i.id} illness={i} />)
      }
    </div>
  );
}

// ── Log modal ─────────────────────────────────────────────────────────────────
function LogModal({ players, user, entry, onSave, onClose }) {
  const [form, setForm] = useState(entry ? {
    player_id: String(entry.player_id ?? ''),
    log_date: entry.log_date?.slice(0, 10) || todayStr(),
    notes: entry.notes || '',
  } : {
    player_id: '',
    log_date: todayStr(),
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.player_id || !form.notes.trim()) return;
    setSaving(true);
    try {
      await onSave({ ...form, notes: form.notes.trim(), created_by: user?.id });
      onClose();
    } finally { setSaving(false); }
  };

  return (
    <Modal title={entry ? 'Edit log entry' : 'Add log entry'} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Field label="Player">
          <select style={selectStyle} value={form.player_id} onChange={e => set('player_id', e.target.value)}>
            <option value="">— select —</option>
            {players.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
        <Field label="Date">
          <input style={inputStyle} type="date" value={form.log_date} onChange={e => set('log_date', e.target.value)} />
        </Field>
        <Field label="Notes">
          <textarea
            style={{ ...inputStyle, minHeight: 100, resize: 'vertical' }}
            placeholder="What happened today with this player?"
            value={form.notes}
            onChange={e => set('notes', e.target.value)}
            autoFocus
          />
        </Field>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
        <button style={btn(false)} onClick={onClose}>Cancel</button>
        <button style={btn(true)} onClick={handleSave} disabled={saving || !form.player_id || !form.notes.trim()}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </Modal>
  );
}

// ── Log tab ───────────────────────────────────────────────────────────────────
function LogTab({ user, players }) {
  const [logs, setLogs]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [filterPlayer, setFilterPlayer] = useUrlParam('player_id', '');
  const [filterDate, setFilterDate]     = useUrlParam('date', '');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getMedicalLog();
      setLogs(data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (data) => {
    await createMedicalLog(data);
    load();
  };

  const handleUpdate = async (data) => {
    await updateMedicalLog(editEntry.id, data);
    load();
  };

  const handleDelete = async (id) => {
    await deleteMedicalLog(id);
    setLogs(l => l.filter(e => e.id !== id));
  };

  const filtered = useMemo(() => {
    return logs
      .filter(e => !filterPlayer || String(e.player_id) === filterPlayer)
      .filter(e => !filterDate   || e.log_date?.slice(0, 10) === filterDate);
  }, [logs, filterPlayer, filterDate]);

  // Group by date
  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach(e => {
      const d = e.log_date?.slice(0, 10) || '—';
      if (!map[d]) map[d] = [];
      map[d].push(e);
    });
    return Object.entries(map).sort((a, b) => b[0].localeCompare(a[0]));
  }, [filtered]);

  const playersInLog = useMemo(() => {
    const ids = new Set(logs.map(e => String(e.player_id)));
    return players.filter(p => ids.has(String(p.id)));
  }, [logs, players]);

  const fmtGroupDate = (d) => {
    if (!d || d === '—') return '—';
    const dt = new Date(d + 'T00:00:00');
    return dt.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <button style={btn(true)} onClick={() => setShowModal(true)}>
          <Plus size={14} style={{ marginRight: 4 }} />Add log
        </button>
        <select
          style={{ ...inputStyle, width: 220 }}
          value={filterPlayer}
          onChange={e => setFilterPlayer(e.target.value)}
        >
          <option value="">All players</option>
          {playersInLog.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <input
          style={{ ...inputStyle, width: 170 }}
          type="date"
          value={filterDate}
          onChange={e => setFilterDate(e.target.value)}
          title="Filter by date"
        />
        {(filterPlayer || filterDate) && (
          <button style={btn(false, '#64748b')} onClick={() => { setFilterPlayer(''); setFilterDate(''); }}>
            Clear filters
          </button>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', color: 'var(--text-dimmed)', padding: 40 }}>Loading…</div>
      ) : grouped.length === 0 ? (
        <div style={{ textAlign: 'center', color: 'var(--text-dimmed)', padding: 40 }}>No log entries found.</div>
      ) : (
        grouped.map(([date, entries]) => (
          <div key={date} style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8, paddingBottom: 6, borderBottom: '1px solid var(--border)' }}>
              {fmtGroupDate(date)}
            </div>
            {entries.map(e => (
              <div key={e.id} style={{ ...card, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, fontSize: 14 }}>{e.player_name}</span>
                    {e.author && (
                      <span style={{ fontSize: 11, color: 'var(--text-dimmed)' }}>by {e.author}</span>
                    )}
                    <span style={{ fontSize: 11, color: 'var(--text-dimmed)' }}>
                      {new Date(e.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                    {e.notes}
                  </div>
                </div>
                <button
                  onClick={() => setEditEntry(e)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dimmed)', padding: 4, flexShrink: 0 }}
                  title="Edit entry"
                >
                  <Edit2 size={14} />
                </button>
                <button
                  onClick={() => handleDelete(e.id)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dimmed)', padding: 4, flexShrink: 0 }}
                  title="Delete entry"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        ))
      )}

      {showModal && (
        <LogModal players={players} user={user} onSave={handleCreate} onClose={() => setShowModal(false)} />
      )}
      {editEntry && (
        <LogModal players={players} user={user} entry={editEntry} onSave={handleUpdate} onClose={() => setEditEntry(null)} />
      )}
    </div>
  );
}

// ── Manage Options tab ────────────────────────────────────────────────────────
function ManageOptionsTab({ options, onRefresh }) {
  const categories = ['body_part', 'injury_type', 'illness_type', 'appointment_type'];
  const labels     = { body_part: 'Body parts', injury_type: 'Injury types', illness_type: 'Illness types', appointment_type: 'Appointment types' };

  const [newInputs, setNewInputs] = useState({});
  const [editId, setEditId]       = useState(null);
  const [editLabel, setEditLabel] = useState('');

  const setNew = (cat, val) => setNewInputs(p => ({ ...p, [cat]: val }));

  const handleAdd = async (cat) => {
    const val = (newInputs[cat] || '').trim();
    if (!val) return;
    await createMedicalOption({ category: cat, label: val });
    setNew(cat, '');
    onRefresh();
  };

  const handleToggle = async (o) => {
    await updateMedicalOption(o.id, { active: !o.active });
    onRefresh();
  };

  const handleEditSave = async (o) => {
    await updateMedicalOption(o.id, { label: editLabel.trim() });
    setEditId(null);
    onRefresh();
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
      {categories.map(cat => {
        const catOptions = options.filter(o => o.category === cat);
        return (
          <div key={cat} style={{ ...card, padding: '14px' }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>{labels[cat]}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {catOptions.map(o => (
                <div key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0' }}>
                  {editId === o.id ? (
                    <>
                      <input style={{ ...inputStyle, flex: 1, padding: '4px 8px', fontSize: 12 }} value={editLabel} onChange={e => setEditLabel(e.target.value)} autoFocus />
                      <button style={{ ...btn(true), padding: '3px 8px' }} onClick={() => handleEditSave(o)}><Check size={12} /></button>
                      <button style={{ ...btn(false), padding: '3px 8px' }} onClick={() => setEditId(null)}><X size={12} /></button>
                    </>
                  ) : (
                    <>
                      <span style={{ flex: 1, fontSize: 13, color: o.active ? 'var(--text-primary)' : 'var(--text-dimmed)', textDecoration: o.active ? 'none' : 'line-through' }}>{o.label}</span>
                      <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 2 }} onClick={() => { setEditId(o.id); setEditLabel(o.label); }}><Edit2 size={11} /></button>
                      <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: o.active ? '#10b981' : '#64748b', padding: 2, fontSize: 11, fontWeight: 700 }} onClick={() => handleToggle(o)}>
                        {o.active ? 'ON' : 'OFF'}
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
              <input
                style={{ ...inputStyle, flex: 1, fontSize: 12 }}
                placeholder="Add new…"
                value={newInputs[cat] || ''}
                onChange={e => setNew(cat, e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAdd(cat)}
              />
              <button style={btn(true)} onClick={() => handleAdd(cat)}><Plus size={13} /></button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Root export ───────────────────────────────────────────────────────────────
export default function Medical({ user }) {
  const [tab, setTab]             = useUrlParam('medical_tab', 'injuries');
  const [players, setPlayers]     = useState([]);
  const [options, setOptions]     = useState([]);
  const [injuries, setInjuries]   = useState([]);
  const [illnesses, setIllnesses] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading]     = useState(true);

  const isPhysioOrAdmin = user?.isAdmin || user?.role === 'physio' || user?.role === 'admin';

  const refresh = useCallback(async () => {
    try {
      const [pl, opts, inj, ill, appts] = await Promise.all([
        getPlayers(),
        getMedicalOptions(),
        getInjuries(),
        getIllnesses(),
        getAppointments(),
      ]);
      setPlayers(pl.filter(p => p.active !== false));
      setOptions(opts);
      setInjuries(inj);
      setIllnesses(ill);
      setAppointments(appts);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const TABS = [
    { id: 'injuries',     label: 'Injuries' },
    { id: 'illness',      label: 'Illness' },
    { id: 'appointments', label: 'Appointments' },
    { id: 'log',          label: 'Log' },
    { id: 'history',      label: 'History' },
    ...(isPhysioOrAdmin ? [{ id: 'manage', label: 'Manage Options' }] : []),
  ];

  // Active counts for tab badges
  const activeInjuries  = injuries.filter(i => !i.is_closed).length;
  const activeIllnesses = illnesses.filter(i => i.status !== 'cleared').length;
  const upcomingAppts   = appointments.filter(a => new Date(a.appointment_date) >= new Date() && a.status === 'scheduled').length;

  if (loading) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '50vh', color: 'var(--text-muted)' }}>Loading medical records…</div>;
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      {/* Tab bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 20, borderBottom: '1px solid var(--border)', paddingBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{ position: 'relative', ...btn(tab === t.id), display: 'flex', alignItems: 'center', gap: 6 }}>
              {t.label}
              {t.id === 'injuries'     && activeInjuries > 0  && <span style={{ fontSize: 10, background: '#ef4444', color: '#fff', borderRadius: 99, padding: '0 5px', fontWeight: 700 }}>{activeInjuries}</span>}
              {t.id === 'illness'      && activeIllnesses > 0 && <span style={{ fontSize: 10, background: '#f59e0b', color: '#000', borderRadius: 99, padding: '0 5px', fontWeight: 700 }}>{activeIllnesses}</span>}
              {t.id === 'appointments' && upcomingAppts > 0   && <span style={{ fontSize: 10, background: '#3b82f6', color: '#fff', borderRadius: 99, padding: '0 5px', fontWeight: 700 }}>{upcomingAppts}</span>}
            </button>
          ))}
        </div>
        <a
          href="/board" target="_blank" rel="noopener noreferrer"
          style={{ ...btn(false, '#3b82f6'), textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          📺 TV Board ↗
        </a>
      </div>

      {tab === 'injuries'     && <InjuriesTab     user={user} players={players} options={options} injuries={injuries}       onRefresh={refresh} />}
      {tab === 'illness'      && <IllnessTab       user={user} players={players} options={options} illnesses={illnesses}     onRefresh={refresh} />}
      {tab === 'appointments' && <AppointmentsTab  user={user} players={players} options={options} appointments={appointments} injuries={injuries} illnesses={illnesses} onRefresh={refresh} />}
      {tab === 'log'          && <LogTab           user={user} players={players} />}
      {tab === 'history'      && <HistoryTab       players={players} injuries={injuries} illnesses={illnesses} />}
      {tab === 'manage'       && <ManageOptionsTab options={options} onRefresh={refresh} />}
    </div>
  );
}
